import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Heart, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from './ui/sheet';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'CPR Training', path: '/cpr-training' },
  { name: 'Blended Courses', path: '/blended-courses' },
  { name: 'Group Classes', path: '/group-classes' },
  { name: 'Mobile Notary', path: '/mobile-notary' },
  { name: 'LifeRest Care', path: '/liferest-care' },
  { name: 'Zoom AHA', path: '/virtual-cpr', highlight: true },
  { name: 'Events', path: '/events' },
  { name: 'Shop', path: '/shop' },
  { name: 'About', path: '/about' },
  { name: 'FAQs', path: '/faqs' },
  { name: 'Contact', path: '/contact' },
  { name: 'Bookings', path: '/admin/bookings' },
  { name: 'Notary Journal', path: '/admin/notary-journal' },
];

export const Navbar = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_quickcert-ga/artifacts/ccwipicm_LifeBreath%20CPR%20%26%20First%20Aid%20logo%20%282%29.png" 
              alt="LifeBreath CPR & First Aid" 
              className="h-14 md:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  link.highlight
                    ? 'bg-gradient-to-r from-[#5a2b8a] to-[#4f62c8] text-white hover:from-[#4a2370] hover:to-[#3a4db8]'
                    : location.pathname === link.path
                      ? 'text-primary bg-primary/10'
                      : 'text-muted-foreground hover:text-primary hover:bg-primary/5'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a href="tel:912-388-6305" className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              <Phone className="w-4 h-4" />
              <span>912-388-6305</span>
            </a>
            <Link to="/book-now">
              <Button data-testid="book-now-btn" className="rounded-full bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] hover:from-[#a51828] hover:to-[#4a2370] shadow-lg shadow-[#c31c31]/25">
                Book Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[350px]">
              <div className="flex flex-col gap-6 mt-6">
                <div className="flex items-center gap-2">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_quickcert-ga/artifacts/ccwipicm_LifeBreath%20CPR%20%26%20First%20Aid%20logo%20%282%29.png" 
                    alt="LifeBreath CPR & First Aid" 
                    className="h-14 w-auto"
                  />
                </div>
                <nav className="flex flex-col gap-2">
                  {navLinks.map((link) => (
                    <SheetClose asChild key={link.path}>
                      <Link
                        to={link.path}
                        data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                          location.pathname === link.path
                            ? 'text-primary bg-primary/10'
                            : 'text-muted-foreground hover:text-primary hover:bg-primary/5'
                        }`}
                      >
                        {link.name}
                      </Link>
                    </SheetClose>
                  ))}
                </nav>
                <div className="flex flex-col gap-3 pt-4 border-t">
                  <a 
                    href="tel:912-388-6305" 
                    className="flex items-center gap-2 px-4 py-3 rounded-lg bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] text-white font-medium"
                  >
                    <Phone className="w-5 h-5" />
                    <span>912-388-6305</span>
                  </a>
                  <SheetClose asChild>
                    <Link to="/book-now">
                      <Button className="w-full rounded-full bg-gradient-to-r from-[#5a2b8a] to-[#4f62c8] hover:from-[#4a2370] hover:to-[#3f52b8]" data-testid="mobile-book-now-btn">
                        Book Now
                      </Button>
                    </Link>
                  </SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
